/**
 * Helper function to safely get an entity's collection.
 * Ensures consistent collection checking across all traversal functions.
 */
export const getEntityCollection = (entity: Record<string, unknown>): Array<Record<string, unknown>> | null => {
  if ('collection' in entity && Array.isArray(entity.collection)) {
    return entity.collection as Array<Record<string, unknown>>;
  }
  return null;
};

/**
 * Generic traversal function for entities with collections.
 * This is the core traversal logic used by DataModelContext.traverseEntity.
 * Extracted here so it can be used before the context is initialized.
 *
 * Note: DataModelContext.traverseEntity adds hierarchyId generation and group filtering on top of this.
 */
export const traversePageEntity = (
  entity: Record<string, unknown>,
  callback: (entity: Record<string, unknown>) => void
): void => {
  callback(entity);

  const collection = getEntityCollection(entity);
  if (collection) {
    collection.forEach((child: Record<string, unknown>) => {
      traversePageEntity(child, callback);
    });
  }
};

/**
 * Removes all content items (articles, collections, queries, linked items) from a layout.
 * Used when cloning a layout to preserve structure while starting with empty content.
 * Works with any container type that has a collection structure.
 */
export const cleanLayoutContent = <T extends object>(root: T): T => {
  // Deep clone to avoid mutating the original
  const cleanedRoot = JSON.parse(JSON.stringify(root)) as T;

  traversePageEntity(cleanedRoot as unknown as Record<string, unknown>, (entity) => {
    // If entity has a collection, filter out content items
    const collection = getEntityCollection(entity);
    if (collection) {
      const filtered = collection.filter((item: Record<string, unknown>) => {
        const attributes = item.attributes as Record<string, unknown> | undefined;

        // Keep everything except articles, collections, queries, and linked items
        const isContentItem =
          (item.type === 'Article' && attributes && 'id' in attributes) ||
          (item.type === 'Collection' && attributes && 'id' in attributes) ||
          (item.type === 'Query' && attributes && 'query' in attributes) ||
          item.type === 'LinkedItem';

        return !isContentItem;
      });
      // eslint-disable-next-line no-param-reassign
      (entity as { collection: Array<Record<string, unknown>> }).collection = filtered;
    }
  });

  return cleanedRoot;
};

/**
 * Resets the layoutId attribute to 'baseline' on the first item in the root collection.
 * Used when cloning a layout to ensure the new page uses the baseline layout.
 */
export const resetLayoutIdToBaseline = <T extends object>(root: T): T => {
  if (!('collection' in root) || !Array.isArray(root.collection) || root.collection.length === 0) {
    return root;
  }

  const firstItem = root.collection[0] as Record<string, unknown>;
  if (!('attributes' in firstItem)) {
    return root;
  }

  return {
    ...root,
    collection: [
      {
        ...firstItem,
        attributes: {
          ...(firstItem.attributes as Record<string, unknown>),
          layoutId: 'baseline'
        }
      },
      ...(root.collection.slice(1) as Array<Record<string, unknown>>)
    ]
  } as T;
};

/**
 * Checks if a page root contains any MultiZoneModule entities.
 * Used to determine if clone layout functionality should be disabled.
 */
export const hasMultiZoneModules = (root: unknown): boolean => {
  if (!root || typeof root !== 'object') {
    return false;
  }

  let hasMultiZone = false;

  traversePageEntity(root as Record<string, unknown>, (entity) => {
    if (entity.subtype === 'MultiZoneModule') {
      hasMultiZone = true;
    }
  });

  return hasMultiZone;
};
